import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Sparkles, Mail, Lock, User, Eye, EyeOff, AlertCircle, Check, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { getApiUrl, getApiBase } from '../utils';
import { Capacitor } from '@capacitor/core';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import { Browser } from '@capacitor/browser';
import { setRefreshToken } from '../utils/secureStorage';

const API_URL = getApiUrl();
const isNativeIOS = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';

// Google icon component
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// Apple icon component
const AppleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
);

export default function Register() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState(null);
  const [error, setError] = useState('');
  const [socialConfig, setSocialConfig] = useState({ google: { enabled: false }, apple: { enabled: false } });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  // Password requirements
  const passwordChecks = {
    length: formData.password.length >= 8,
    uppercase: /[A-Z]/.test(formData.password),
    lowercase: /[a-z]/.test(formData.password),
    number: /[0-9]/.test(formData.password)
  };
  const isPasswordValid = Object.values(passwordChecks).every(Boolean);
  const passwordsMatch = formData.password === formData.confirmPassword && formData.confirmPassword.length > 0;

  // Fetch social login configuration
  useEffect(() => {
    const fetchSocialConfig = async () => {
      try {
        const res = await axios.get(`${API_URL}/auth/social-status`);
        setSocialConfig(res.data);
      } catch (e) {
        console.log('Social login config not available');
      }
    };
    fetchSocialConfig();
    
    // Load Google Identity Services script for web only (doesn't work in iOS WKWebView)
    if (!isNativeIOS) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => setGisScriptLoaded(true);
      document.body.appendChild(script);
      return () => {
        if (document.body.contains(script)) document.body.removeChild(script);
      };
    }
  }, []);

  // Track when GIS script has loaded
  const [gisScriptLoaded, setGisScriptLoaded] = useState(false);
  const [googleTokenClient, setGoogleTokenClient] = useState(null);

  // Initialize Google OAuth2 token client once script + config are ready
  useEffect(() => {
    if (gisScriptLoaded && socialConfig.google.enabled && window.google) {
      try {
        const client = window.google.accounts.oauth2.initTokenClient({
          client_id: socialConfig.google.clientId,
          scope: 'email profile',
          callback: async (tokenResponse) => {
            if (tokenResponse.error) {
              console.error('Google OAuth error:', tokenResponse);
              setError('Google sign-up failed. Please try again.');
              setSocialLoading(null);
              return;
            }
            try {
              const res = await axios.post(`${API_URL}/auth/google`, {
                accessToken: tokenResponse.access_token
              });
              localStorage.setItem('token', res.data.token);
              localStorage.setItem('user', JSON.stringify(res.data.user));
              await setRefreshToken(res.data.refreshToken);
              navigate('/home');
              window.location.reload();
            } catch (err) {
              const errData = err.response?.data?.error;
              setError(typeof errData === 'object' ? (errData.message || 'Google sign-up failed') : (errData || 'Google sign-up failed'));
            } finally {
              setSocialLoading(null);
            }
          },
        });
        setGoogleTokenClient(client);
        console.log('✅ Google OAuth2 token client initialized');
      } catch (e) {
        console.log('Google Sign-In init error:', e);
      }
    }
  }, [gisScriptLoaded, socialConfig.google.enabled, socialConfig.google.clientId]);

  const handleGoogleClick = async () => {
    setSocialLoading('google');
    setError('');
    try {
      if (isNativeIOS) {
        // iOS: Open backend OAuth redirect URL in Safari View Controller
        const baseUrl = getApiBase();
        await Browser.open({
          url: `${baseUrl}/api/auth/google/redirect`,
          presentationStyle: 'popover'
        });
        // The callback will be handled by App.js deep link listener
        setTimeout(() => setSocialLoading(null), 30000);
      } else if (googleTokenClient) {
        // Web: Use GIS OAuth2 popup
        googleTokenClient.requestAccessToken();
      } else {
        setError('Google Sign-In is not available right now. Please use email registration.');
        setSocialLoading(null);
      }
    } catch (e) {
      console.error('Google Sign-In error:', e);
      setError('Google Sign-In is not available right now. Please use email registration.');
      setSocialLoading(null);
    }
  };

  const handleAppleSignup = async () => {
    if (!isNativeIOS) {
      setError('Apple Sign-In is only available in the iOS app.');
      return;
    }
    setSocialLoading('apple');
    setError('');
    try {
      const result = await SignInWithApple.authorize({
        clientId: 'com.fashiondisc.app',
        redirectURI: '',
        scopes: 'email name',
        state: '',
        nonce: '',
      });
      const { identityToken, givenName, familyName, email } = result.response;
      if (!identityToken) {
        throw new Error('No identity token received from Apple');
      }
      const response = await axios.post(`${API_URL}/auth/apple`, {
        identityToken,
        user: {
          name: { firstName: givenName || '', lastName: familyName || '' },
          email: email || ''
        }
      });
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      await setRefreshToken(response.data.refreshToken);
      navigate('/home');
      window.location.reload();
    } catch (err) {
      console.error('Apple Sign-In error:', err);
      if (err?.message?.includes('canceled') || err?.message?.includes('1001')) {
        // User cancelled - don't show error
      } else {
        setError(err?.response?.data?.error || 'Apple Sign-Up failed. Please try again.');
      }
    } finally {
      setSocialLoading(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/auth/register`, {
        name: formData.name,
        email: formData.email,
        password: formData.password
      });
      
      // Save token to localStorage
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      await setRefreshToken(response.data.refreshToken);

      // Redirect to home
      navigate('/home');
      window.location.reload(); // Refresh to update auth state
    } catch (err) {
      const errData = err.response?.data?.error;
      setError(typeof errData === 'object' ? (errData.message || 'Registration failed. Please try again.') : (errData || 'Registration failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const passwordStrength = () => {
    const pwd = formData.password;
    if (pwd.length === 0) return null;
    if (pwd.length < 6) return { level: 'weak', color: 'bg-red-500' };
    if (pwd.length < 10) return { level: 'medium', color: 'bg-yellow-500' };
    return { level: 'strong', color: 'bg-olive-500' };
  };

  const strength = passwordStrength();

  return (
    <div className="min-h-screen flex items-center justify-center p-6 pt-safe pb-safe">
      {/* Floating gradient orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{ 
            y: [0, -30, 0],
            x: [0, 20, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ duration: 8, repeat: Infinity }}
          className="absolute top-20 left-20 w-96 h-96 bg-suede-300 rounded-full blur-3xl opacity-20"
        />
        <motion.div
          animate={{ 
            y: [0, 30, 0],
            x: [0, -20, 0],
            scale: [1, 1.2, 1]
          }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute bottom-20 right-20 w-96 h-96 bg-terracotta-300 rounded-full blur-3xl opacity-20"
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/home" className="inline-flex items-center gap-3 mb-4">
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-16 h-16 bg-gradient-to-br from-suede-600 to-suede-500 rounded-2xl flex items-center justify-center"
            >
              <Sparkles className="w-8 h-8 text-white" />
            </motion.div>
          </Link>
          <h1 className="text-3xl font-light text-stone-800">
            Create <span className="bg-gradient-to-r from-suede-600 to-suede-500 bg-clip-text text-transparent font-semibold">Account</span>
          </h1>
          <p className="text-stone-600 mt-2">Join fashionDisc and discover your style</p>
        </div>

        <Card className="glass-card p-8 rounded-2xl border-stone-200/80">
          {/* Social Sign-Up Buttons */}
          <div className="space-y-3 mb-6">
            {/* Google Sign-Up — custom button */}
            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleClick}
              disabled={socialLoading === 'google' || !socialConfig.google.enabled}
              className={`w-full py-6 text-base rounded-2xl border-stone-200/80 ${
                socialConfig.google.enabled 
                  ? 'hover:bg-stone-50/80 cursor-pointer' 
                  : 'bg-stone-50/50 opacity-60 cursor-not-allowed'
              }`}
            >
              {socialLoading === 'google' ? (
                <span className="flex items-center justify-center gap-2">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="w-5 h-5 border-2 border-stone-600 border-t-transparent rounded-full"
                  />
                  Signing up...
                </span>
              ) : (
                <>
                  <GoogleIcon />
                  <span className="ml-3 text-stone-700">Sign up with Google</span>
                </>
              )}
            </Button>

            {isNativeIOS ? (
              <Button
                type="button"
                variant="outline"
                onClick={handleAppleSignup}
                disabled={socialLoading === 'apple'}
                className="w-full py-6 text-base rounded-2xl border-stone-200/80 hover:bg-stone-50/80"
              >
                {socialLoading === 'apple' ? (
                  <span className="flex items-center justify-center gap-2">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      className="w-5 h-5 border-2 border-stone-600 border-t-transparent rounded-full"
                    />
                    Signing up...
                  </span>
                ) : (
                  <>
                    <AppleIcon />
                    <span className="ml-3 text-stone-700">Sign up with Apple</span>
                  </>
                )}
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                disabled={true}
                className="w-full py-6 text-base rounded-2xl border-stone-200/80 bg-stone-50/50 opacity-60 cursor-not-allowed"
              >
                <AppleIcon />
                <span className="ml-3 text-stone-400">Apple Sign-Up (iOS App Only)</span>
              </Button>
            )}
          </div>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-stone-200/80"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white/60 backdrop-blur-sm rounded-full text-stone-500">or with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-red-50/80 border border-red-200 rounded-2xl flex items-start gap-3"
              >
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </motion.div>
            )}

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Jane Doe"
                  className="pl-12 py-6 text-base"
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="pl-12 py-6 text-base"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="pl-12 pr-12 py-6 text-base"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              
              {/* Password Strength */}
              {strength && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    <div className={`h-1 flex-1 rounded ${strength.color}`} />
                    <div className={`h-1 flex-1 rounded ${strength.level !== 'weak' ? strength.color : 'bg-stone-200'}`} />
                    <div className={`h-1 flex-1 rounded ${strength.level === 'strong' ? strength.color : 'bg-stone-200'}`} />
                  </div>
                  <p className={`text-xs ${
                    strength.level === 'weak' ? 'text-red-600' :
                    strength.level === 'medium' ? 'text-yellow-600' :
                    'text-olive-600'
                  }`}>
                    Password strength: {strength.level}
                  </p>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="pl-12 py-6 text-base"
                  required
                />
                {formData.confirmPassword && formData.password === formData.confirmPassword && (
                  <CheckCircle className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-olive-600" />
                )}
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full py-6 text-base rounded-2xl bg-gradient-to-r from-suede-600 to-suede-500 hover:shadow-xl transition-all"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                  />
                  Creating account...
                </span>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          {/* Login Link */}
          <div className="mt-6 pt-6 border-t border-stone-200/80 text-center">
            <p className="text-sm text-stone-600">
              Already have an account?{' '}
              <Link 
                to="/login" 
                className="font-medium text-suede-600 hover:text-suede-700"
              >
                Sign in
              </Link>
            </p>
          </div>
        </Card>

        {/* Terms */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-6 text-center text-xs text-stone-500"
        >
          By creating an account, you agree to our{' '}
          <Link to="/terms" className="underline text-suede-600 hover:text-suede-700">Terms of Service</Link>
          {' '}and{' '}
          <Link to="/privacy" className="underline text-suede-600 hover:text-suede-700">Privacy Policy</Link>
        </motion.p>
      </motion.div>
    </div>
  );
}
